/**
 * Reusable page-selection modal. Used in two flows:
 *   - mode: 'multi'  — pick a subset of pages to load from a template
 *   - mode: 'single' — pick one page (radio) when adding a page from a template
 *
 * CB-10: rides ui/collection-browser.js for the row list, the filter field,
 * in-place filtering, the empty state and the selection store. It keeps the
 * export modal's `pb-export-*` classes (those rules are SHARED with Export
 * Settings, which is not migrating, so nothing about them moves) and it keeps
 * its own footer, because the confirm/cancel pair and the disabled-at-zero
 * rule are this dialog's contract, not the module's.
 *
 * Two distinctions this file exists to keep straight:
 *
 *  1. **Single mode is a RADIO, unpainted.** Same `data-cb-action` hook, same
 *     slot, but no `pb-checkbox-input`: painting a radio as a ticked square
 *     states the wrong exclusivity. The module carries it through
 *     `selection.mode: 'single'`.
 *  2. **The three shortcuts are whole-set predicates, not selections of the
 *     visible set.** They are `toolbarActions` — each computes a set from the
 *     FULL record list and calls `api.setSelection()` — and they are
 *     deliberately neither `bulkIntents` nor `selectAllVisible`. With a filter
 *     active, "All Pages" still means every page, while the select-all
 *     checkbox still means only what you can see. Two different affordances on
 *     one surface; conflating them is the failure this split prevents.
 */

import { createButton } from './components.js';
import { openCollectionBrowser } from './collection-browser.js';
import { normalizePageEntries } from './page-selection-utils.js';

/**
 * @param {object} opts
 * @param {string} opts.title
 * @param {Array<object>} opts.pages — page-map entries: { name, filename, sections }
 * @param {'multi'|'single'} opts.mode
 * @param {string} opts.confirmLabel
 * @param {'all'|'first'} [opts.defaultSelection]
 * @param {(filenames: string[]) => void} opts.onConfirm
 */
export function showPageSelectionModal({
  title,
  pages,
  mode,
  confirmLabel,
  defaultSelection = mode === 'multi' ? 'all' : 'first',
  onConfirm
}) {
  const pageEntries = normalizePageEntries(pages, { idField: 'filename' });
  if (pageEntries.length === 0) return;

  const isMulti = mode === 'multi';
  let api = null;
  let confirmBtn = null;

  /** Document order, always — never click order. */
  function selectedInOrder() {
    const picked = new Set(api?.getSelectedIds() || []);
    return pageEntries.filter((entry) => picked.has(entry.id)).map((entry) => entry.id);
  }

  function renderSummary() {
    const count = api?.getSelectedIds().length || 0;
    if (confirmBtn) {
      // aria-disabled alongside the property: a disabled button is silent to
      // assistive tech about WHY it cannot be pressed unless it is announced.
      confirmBtn.disabled = count === 0;
      confirmBtn.setAttribute('aria-disabled', String(count === 0));
    }
  }

  api = openCollectionBrowser({
    title,
    initialFocus: 'search', // PP-2: search owns focus at time zero, through the kit
    testid: 'page-selection',
    // The donor's box: `.pb-modal-export-settings` caps at 620px, and this
    // dialog is a short row list, not a library.
    size: null,
    maxWidth: 620,
    overlayClassName: 'pb-page-selection-overlay',
    className: 'pb-modal-export-settings',
    layout: 'rows',
    listTag: 'div',
    itemTag: 'div',
    // EXPLICIT, never defaulted: these rows are a group of form controls, not
    // a list of things — which is what they were before the migration and what
    // `role="group"` + the mode-specific label says.
    listRole: 'group',
    listAriaLabel: `Page ${isMulti ? 'checkboxes' : 'options'}`,
    search: { placeholder: 'Filter pages…', ariaLabel: 'Filter pages' },
    chips: false,
    headerMeta: false,
    skeleton: { rowVariant: 'revision', count: () => pageEntries.length },
    illustration: 'pages',
    emptyCopy: { title: 'No pages to choose from.' },
    filteredEmptyCopy: { title: 'No pages match your filter.' },

    // Synchronous data — the caller already has the page map in hand.
    load: async () => pageEntries,
    idOf: (entry) => entry.id,

    adapt: (entry) => {
      const metaParts = [];
      if (entry.isHome) metaParts.push('Home');
      if (entry.isLikelyBlog) metaParts.push('Blog');
      if (entry.isHome) metaParts.push('/');
      else if (entry.slug) metaParts.push(`/${entry.slug}/`);
      // The surface keeps its own two-line copy block, so the export modal's
      // `.pb-export-page-*` rules (shared with Export Settings, which is not
      // migrating) go on rendering it unchanged.
      const copy = document.createElement('span');
      copy.className = 'pb-export-page-copy';
      const name = document.createElement('span');
      name.className = 'pb-export-page-name';
      name.textContent = entry.name;
      const meta = document.createElement('span');
      meta.className = 'pb-export-page-meta';
      meta.textContent = metaParts.join(' • ');
      copy.append(name, meta);
      return {
        id: entry.id,
        title: entry.name,
        searchText: entry.searchText,
        className: 'pb-export-page-row',
        content: copy,
        data: entry
      };
    },

    selection: {
      noun: 'page',
      mode: isMulti ? 'multi' : 'single',
      // Single mode: exactly one row is picked at all times, so there is no
      // empty selection to announce and nothing to act on in bulk. The rows say
      // which page is chosen; the footer's Add Page is the action.
      selectAll: isMulti,
      // The row is the click target, as it was when it was a <label>.
      rowClick: true,
      onChange: renderSummary
    },

    // The donor's own row of shortcut buttons keeps its class, so the export
    // modal's flex/gap/margin rule goes on describing it.
    toolbarActionsClass: 'pb-export-pages-actions',

    // Whole-set predicates. NOT bulkIntents (they act on the selection) and NOT
    // selectAllVisible (that is filter-scoped, and correctly so).
    toolbarActions: isMulti
      ? [
          {
            id: 'select-all-pages',
            label: 'All Pages',
            compute: (records) => records.map((entry) => entry.id)
          },
          {
            id: 'select-home-page',
            label: 'Homepage Only',
            compute: (records) => (records[0] ? [records[0].id] : [])
          },
          {
            id: 'exclude-blog-pages',
            label: 'Exclude Blog Pages',
            compute: (records) => {
              const keep = records
                .filter((entry) => !entry.isLikelyBlog || entry.isHome)
                .map((entry) => entry.id);
              // Never leave the dialog with nothing selectable: excluding every
              // page would disable Confirm and read as a broken button.
              if (keep.length === 0 && records[0]) keep.push(records[0].id);
              return keep;
            }
          }
        ]
      : undefined,

    onOpen(handle) {
      api = handle;
      handle.body.classList.add('pb-export-modal-body');
      const shell = handle.listEl.closest('.pb-coll-shell');
      // The donor's bordered, 220px-capped box wraps the ROWS. It goes on the
      // list, not on the module's scroller: the scroller carries the bulk
      // bar's reserved strip, and 64px of reserve inside a bordered 220px box
      // is dead space in the wrong element — the bar floats over the modal
      // card, not over the list.
      handle.listEl.classList.add('pb-export-pages-list');

      // Single selection has no bulk bar; use the chrome's existing live region.
      if (!isMulti) shell?.querySelector('[role="status"]')?.classList.remove('pb-sr-only');

      // Footer through the kit. The hand-written pb-btn class strings this file
      // used to emit are what kept it on the sweep's no-hand-rolled-btn-class
      // allowlist — quoting one here verbatim would keep the entry alive for a
      // comment, which is why this sentence names the rule instead.
      const footer = document.createElement('div');
      footer.className = 'pb-modal-footer pb-export-modal-footer';
      confirmBtn = createButton({
        variant: 'primary',
        label: confirmLabel,
        onClick: () => {
          if (confirmBtn.disabled) return;
          const selected = selectedInOrder();
          if (selected.length === 0) return;
          handle.close();
          onConfirm(selected);
        }
      });
      footer.append(
        createButton({ variant: 'ghost', label: 'Cancel', onClick: () => handle.close() }),
        confirmBtn
      );
      handle.modal.appendChild(footer);
    },

    onRender(handle) {
      // The control keeps the export modal's denser 14px box. The class covers
      // the radio too — its own rule says so, and the radio deliberately has no
      // kit paint to inherit sizing from.
      for (const input of handle.listEl.querySelectorAll('input[data-cb-action="__toggle"]')) {
        input.classList.add('pb-export-page-checkbox');
      }
      // The seed selection, applied once the rows exist: 'all' or the first
      // page in multi, always the first page in single.
      if (handle.getSelectedIds().length === 0) {
        const seed =
          isMulti && defaultSelection === 'all'
            ? pageEntries.map((entry) => entry.id)
            : pageEntries[0]
              ? [pageEntries[0].id]
              : [];
        handle.setSelection(seed);
      }
      renderSummary();
    }
  });
  if (!api) return;
}
